import type {
  FulfillmentCarrierFeed,
  FulfillmentCarrierSnapshotEntry,
  FulfillmentTracking,
  FulfillmentTrackingEventEntry,
} from '@/infrastructure/FulfillmentAdminClient';
import type { TrackingMoreCheckpoint, TrackingMoreDetail } from '@/models/FedExTrackingDetail';
import { asRecord, asText } from '@/utils/fulfillmentDeleoLabel';
import { instantMs } from '@/utils/fulfillmentTime';

/**
 * Readers for the carrier snapshot V2 tracking intake keeps on a tracking row (SSD-7091). Each feed's `data` is the
 * carrier's own payload as it arrived, so every field is read defensively: a missing one shows as a dash, never a crash.
 */

export type CarrierKind = 'YUN_EXPRESS' | 'FEDEX' | 'DELEO' | 'PARXL' | 'OTHER';

export const carrierKindOf = (carrier: string | null | undefined): CarrierKind => {
  const name = (carrier ?? '').toUpperCase();
  if (name === 'YUN_EXPRESS' || name === 'YUN') {
    return 'YUN_EXPRESS';
  }
  if (name === 'FEDEX' || name === 'DELEO' || name === 'PARXL') {
    return name;
  }
  return 'OTHER';
};

/** The order's own parcels; inbound rows reference a line (`orderId#lineNo`) and carry no carrier history. */
export const outboundTrackingOf = (orderId: number, tracking: FulfillmentTracking[]): FulfillmentTracking[] =>
  tracking.filter((row) => row.refId === String(orderId));

export const feedOf = (
  row: FulfillmentTracking,
  feed: FulfillmentCarrierFeed,
): FulfillmentCarrierSnapshotEntry | null => row.snapshot?.carrierSnapshot?.[feed] ?? null;

const textOf = (value: unknown): string | null => asText(value) ?? null;

/** Newest first; a stamp nobody can read sinks to the bottom rather than jumping to the top. */
const byNewest =
  <T>(timeOf: (item: T) => string | null | undefined) =>
  (left: T, right: T): number =>
    (instantMs(timeOf(right)) ?? -Infinity) - (instantMs(timeOf(left)) ?? -Infinity);

export interface YunTrackEvent {
  /** The carrier's local time, as YunExpress reports it (no zone). */
  localTime: string | null;
  utcTime: string | null;
  node: string | null;
  description: string | null;
  location: string | null;
}

export interface YunTracking {
  waybillNumber: string | null;
  lastMileNumber: string | null;
  lastMileName: string | null;
  lastMileSite: string | null;
  packageStatus: string | null;
  syncedAt: string;
  events: YunTrackEvent[];
}

/** The YunExpress push as V2 keeps it: snake_case track events plus the last-mile carrier. */
export const yunTrackingOf = (row: FulfillmentTracking): YunTracking | null => {
  const entry = feedOf(row, 'YUN_EXPRESS');
  if (!entry) {
    return null;
  }
  const data = entry.data ?? {};
  const rawEvents = Array.isArray(data['trackEvents']) ? data['trackEvents'] : [];
  const events = rawEvents
    .map(asRecord)
    .filter((event): event is Record<string, unknown> => event !== null)
    .map((event) => ({
      localTime: textOf(event['process_time']),
      utcTime: textOf(event['process_utc_time']),
      node: textOf(event['track_node_code']),
      description: textOf(event['process_content']),
      location:
        textOf(event['process_location']) ??
        ([event['process_city'], event['process_province'], event['process_country']]
          .map(textOf)
          .filter(Boolean)
          .join(', ') ||
          null),
    }))
    // The UTC stamp orders events across zones; the local one is the fallback YunExpress sometimes sends alone.
    .sort(byNewest((event) => event.utcTime ?? event.localTime));
  return {
    waybillNumber: textOf(data['waybillNumber']),
    lastMileNumber: textOf(data['trackingNumber']),
    lastMileName: textOf(data['lastMileName']),
    lastMileSite: textOf(data['lastMileSite']),
    packageStatus: entry.providerStatus ?? textOf(data['packageStatus']),
    syncedAt: entry.receivedAt,
    events,
  };
};

/** TrackingMore's report in the shape OMS Center's timeline reads, when it is about this parcel and has a dated checkpoint. */
export const trackingMoreOf = (row: FulfillmentTracking): TrackingMoreDetail | null => {
  const entry = feedOf(row, 'TRACKING_MORE');
  if (!entry) {
    return null;
  }
  const data = entry.data ?? {};
  const checkpoints: TrackingMoreCheckpoint[] = (Array.isArray(data['checkpoints']) ? data['checkpoints'] : [])
    .map(asRecord)
    .filter((checkpoint): checkpoint is Record<string, unknown> => checkpoint !== null)
    .map((checkpoint) => {
      const location = textOf(checkpoint['location']);
      return {
        checkpointDate: textOf(checkpoint['checkpointDate']) ?? '',
        deliveryStatus: textOf(checkpoint['deliveryStatus']) ?? '',
        deliverySubstatus: textOf(checkpoint['deliverySubstatus']) ?? '',
        trackingDetail: textOf(checkpoint['trackingDetail']) ?? '',
        ...(location !== null && { location }),
      };
    });
  const trackingNumber = textOf(data['trackingNumber']);
  // A snapshot taken for an earlier number is not this parcel's history.
  if (trackingNumber !== null && trackingNumber !== row.number) {
    return null;
  }
  if (!checkpoints.some((checkpoint) => Number.isFinite(Date.parse(checkpoint.checkpointDate)))) {
    return null;
  }
  const latestEvent = textOf(data['latestEvent']);
  const latestCheckpointTime = textOf(data['latestCheckpointTime']);
  return {
    trackingNumber: trackingNumber ?? row.number,
    courierCode: textOf(data['courierCode']) ?? '',
    deliveryStatus: textOf(data['deliveryStatus']) ?? '',
    substatus: textOf(data['substatus']) ?? '',
    ...(latestEvent !== null && { latestEvent }),
    ...(latestCheckpointTime !== null && { latestCheckpointTime }),
    checkpoints,
  };
};

export interface DeleoStatus {
  code: string | null;
  name: string | null;
}

export interface DeleoHandOver {
  carrierName: string | null;
  trackingNo: string | null;
  requestId: string | null;
  createdOn: string | null;
  updatedOn: string | null;
  wmsAsnCode: string | null;
  deleoTrackingNo: string | null;
}

export interface DeleoTraceRow {
  trackingNo: string | null;
  eventTime: string | null;
  status: string | null;
  description: string | null;
  location: string | null;
}

export interface DeleoTracking {
  deleoOrderId: string | null;
  deleoTrackingNo: string | null;
  status: DeleoStatus;
  syncedAt: string;
  handOvers: DeleoHandOver[];
  trace: DeleoTraceRow[];
}

const nameOf = (value: unknown): string | null => textOf(asRecord(value)?.['name']) ?? textOf(value);

/** The Deleo order and its delivery trace as the last webhook read them. */
export const deleoTrackingOf = (row: FulfillmentTracking): DeleoTracking | null => {
  const entry = feedOf(row, 'DELEO');
  if (!entry) {
    return null;
  }
  const data = entry.data ?? {};
  const status = asRecord(data['status']);
  const handOvers = (Array.isArray(data['orderTrackingList']) ? data['orderTrackingList'] : [])
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      carrierName: nameOf(item['deliveryCompanyCd']),
      trackingNo: textOf(item['trackingNo']),
      requestId: textOf(item['requestId']),
      createdOn: textOf(item['createdOn']),
      updatedOn: textOf(item['updatedOn']),
      wmsAsnCode: textOf(item['wmsAsnCd']),
      deleoTrackingNo: textOf(item['deleoTrackingNo']),
    }));
  const traceInfoList = asRecord(data['deliveryTrace'])?.['traceInfoList'];
  const trace = (Array.isArray(traceInfoList) ? traceInfoList : [])
    .map(asRecord)
    .filter((info): info is Record<string, unknown> => info !== null)
    .flatMap((info) =>
      (Array.isArray(info['traceList']) ? info['traceList'] : [])
        .map(asRecord)
        .filter((event): event is Record<string, unknown> => event !== null)
        .map((event) => ({
          trackingNo: textOf(info['trackingNo']),
          eventTime: textOf(event['eventTime']),
          status: textOf(event['status']),
          description: textOf(event['description']),
          location: textOf(event['location']),
        })),
    )
    .filter((event) => event.eventTime !== null)
    .sort(byNewest((event) => event.eventTime));
  return {
    deleoOrderId: textOf(data['deleoOrderId']) ?? row.carrierOrderId ?? null,
    deleoTrackingNo: textOf(data['deleoTrackingNo']),
    status: { code: textOf(status?.['code']), name: textOf(status?.['name']) ?? entry.providerStatus },
    syncedAt: entry.receivedAt,
    handOvers,
    trace,
  };
};

export interface ParxlTrackingEvent {
  date: string | null;
  status: string | null;
  description: string | null;
  location: string | null;
}

/** The stored Parxl tracking (the refresh keeps it, SSD-7104): shown only when the live Parxl answer is missing. */
export const storedParxlEventsOf = (row: FulfillmentTracking): ParxlTrackingEvent[] => {
  const data = feedOf(row, 'PARXL')?.data ?? {};
  return (Array.isArray(data['trackingStatus']) ? data['trackingStatus'] : [])
    .map(asRecord)
    .filter((event): event is Record<string, unknown> => event !== null)
    .map((event) => ({
      date: textOf(event['date']),
      status: textOf(event['status']),
      description: textOf(event['description']),
      location: textOf(event['location']),
    }));
};

/** Unified events newest first. */
export const unifiedEventsOf = (row: FulfillmentTracking): FulfillmentTrackingEventEntry[] =>
  [...(row.events ?? [])].sort(byNewest((event) => event.timestamp));

/** The newest unified event, which the card heads with. */
export const latestEventOf = (row: FulfillmentTracking): FulfillmentTrackingEventEntry | null =>
  unifiedEventsOf(row)[0] ?? null;
